import React, { useState, useEffect } from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '../Icon/Icon.type';
import axios from '../../utils/axios';
import { formatCost } from '../../utils/formatCost';
import ArrowUpRightIcon from '../../assets/icons/arrow-up-right.svg';
import ArrowDownRightIcon from '../../assets/icons/arrow-down-right.svg';
import { formatLatency } from '../../utils/formatLatency';
import { showTokens, showDuration, canShowCost } from '../../utils/usageConfig';
import { useAuth } from '../../contexts/AuthContext';

interface ProjectStatisticsProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  currentMode?: 'design' | 'build' | 'cab';
}

interface ProjectTokenStats {
  total_tokens: number;
  total_cost: number;
  total_requests: number;
  input_tokens?: number;
  output_tokens?: number;
  latency_ms?: number;
  brainstorm_tokens: number;
  brainstorm_cost: number;
  brainstorm_requests: number;
  brainstorm_input_tokens?: number;
  brainstorm_output_tokens?: number;
  brainstorm_latency_ms?: number;
  development_tokens: number;
  development_cost: number;
  development_latency_ms: number;
  development_requests: number;
  development_input_tokens?: number;
  development_output_tokens?: number;
  cab_latency_ms?: number;
  cab_tokens?: number;
  cab_cost?: number;
  cab_requests?: number;
  cab_input_tokens?: number;
  cab_output_tokens?: number;
  cab_review_cost?: number;
  cab_review_tokens?: number;
  cab_review_requests?: number;
  cab_review_input_tokens?: number;
  cab_review_output_tokens?: number;
  cab_review_latency_ms?: number;
  cab_generation_cost?: number;
  ticket_evaluation_tokens?: number;
  ticket_evaluation_cost?: number;
  ticket_evaluation_requests?: number;
  ticket_evaluation_input_tokens?: number;
  ticket_evaluation_output_tokens?: number;
  ticket_evaluation_latency_ms?: number;
  last_token_activity?: string;
}

export const ProjectStatistics: React.FC<ProjectStatisticsProps> = ({
  isOpen,
  onClose,
  projectId,
  currentMode = 'build',
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenStats, setTokenStats] = useState<ProjectTokenStats | null>(null);
  const { hasAdminRole } = useAuth();
  const showCost = canShowCost(hasAdminRole);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchAnalyticsData();
    }
  }, [isOpen, projectId]);

  const fetchAnalyticsData = async () => {
    if (!projectId) {
      setError('No project selected');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/billing/projects/${projectId}/detailed-analytics`);
      console.log('Analytics API Response:', response.data);
      
      // The API returns { token_stats: ProjectTokenStats, ... }
      if (response.data && response.data.token_stats) {
        setTokenStats(response.data.token_stats);
      } else {
        console.warn('No analytics data available');
        setTokenStats(null);
      }
    } catch (err: any) {
      console.error('Failed to fetch analytics data:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const formatTokenCount = (tokens: number): string => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    } else if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  const getModeIcon = (mode: string) => {
    const isDesignMode = mode === 'brainstorm' || mode === 'design';
    const isCABMode = mode === 'cab';
    const isTicketEvaluation = mode === 'ticket_evaluation';
    
    if (isDesignMode) {
      return (
        <div className="w-[28px] h-[28px] flex items-center justify-center">
          <Icon 
            name={IconName.Design} 
            size="xl" 
            className="text-fg-primary"
            inheritColor={true}
          />
        </div>
      );
    } else if (isCABMode) {
      return (
        <div className="w-[28px] h-[28px] flex items-center justify-center">
          <Icon 
            name={IconName.Document} 
            size="xl" 
            className="text-fg-primary"
            inheritColor={true}
          />
        </div>
      );
    } else if (isTicketEvaluation) {
      return (
        <div className="w-[28px] h-[28px] flex items-center justify-center">
          <Icon
            name={IconName.Tickets}
            size="xl"
            className="text-fg-primary"
            inheritColor={true}
          />
        </div>
      );
    } else {
      return (
        <div className="w-[28px] h-[28px] flex items-center justify-center">
          <Icon 
            name={IconName.Code} 
            size="xl" 
            className="text-fg-primary"
            inheritColor={true}
          />
        </div>
      );
    }
  };

  const getModeTitle = (mode: string): string => {
    if (mode === 'brainstorm' || mode === 'design') {
      return 'Prototype Mode';
    } else if (mode === 'development') {
      return 'Build Mode';
    } else if (mode === 'cab') {
      return 'CAB Mode';
    } else if (mode === 'cab_review') {
      return 'CAB AI Edit Review';
    } else if (mode === 'ticket_evaluation') {
      return 'Tickets';
    }
    return mode;
  };

  const getModeDescription = (mode: string): string => {
    if (mode === 'brainstorm' || mode === 'design') {
      return 'UI generation & iterations';
    } else if (mode === 'development') {
      return 'Code generation & debugging';
    } else if (mode === 'cab') {
      return 'Application blueprint generation';
    } else if (mode === 'cab_review') {
      return 'AI-powered edit propagation';
    } else if (mode === 'ticket_evaluation') {
      return 'AI Ticket Generation & Evaluation';
    }
    return '';
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[470px] flex flex-col border-l border-border-secondary-alt overflow-y-auto ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03), 0px 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Header */}
        <div className="flex px-bg-v2-3xl pt-bg-v2-3xl pb-bg-v2-xl items-start gap-bg-v2-md self-stretch">
          <div className="flex flex-col gap-bg-v2-xxs flex-1">
            <Text variant="text-lg" weight="semibold" color="primary">
              Project Statistics
            </Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200 w-8 h-8"
          >
            <Icon name={IconName.Cross} size="md" inheritColor />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col px-bg-v2-xl">
          {/* Divider */}
          <div className="bg-border-secondary h-px w-full" />
          
          <div className="flex flex-col gap-[16px] pt-[16px] pb-bg-v2-xl">

          {loading && (
            <>
              {/* Total Usage Card Skeleton */}
              <div className="bg-bg-tertiary border border-border-secondary rounded-[12px] p-[21px] flex flex-col gap-[16px]">
                <div className="h-5 skeleton-shimmer rounded w-24"></div>
                
                {/* Stats Row Skeleton — Total Cost + Requests */}
                <div className="flex gap-[24px] items-start justify-center">
                  <div className="flex flex-col gap-[4px] items-center flex-1">
                    <div className="h-4 skeleton-shimmer rounded w-16"></div>
                    <div className="h-8 skeleton-shimmer rounded w-20"></div>
                  </div>
                  <div className="flex flex-col gap-[4px] items-center flex-1">
                    <div className="h-4 skeleton-shimmer rounded w-16"></div>
                    <div className="h-8 skeleton-shimmer rounded w-12"></div>
                  </div>
                </div>

                {/* AI Runtime Skeleton */}
                <div className="border-t border-border-primary pt-[17px] flex items-center justify-between">
                  <div className="h-4 skeleton-shimmer rounded w-24"></div>
                  <div className="h-7 skeleton-shimmer rounded w-20"></div>
                </div>

                {/* Token In / Out Skeleton */}
                <div className="border-t border-border-primary pt-[17px] flex gap-[24px]">
                  <div className="flex flex-col gap-[4px] flex-1">
                    <div className="h-4 skeleton-shimmer rounded w-14 mx-auto"></div>
                    <div className="h-7 skeleton-shimmer rounded w-16 mx-auto"></div>
                  </div>
                  <div className="flex flex-col gap-[4px] flex-1">
                    <div className="h-4 skeleton-shimmer rounded w-14 mx-auto"></div>
                    <div className="h-7 skeleton-shimmer rounded w-16 mx-auto"></div>
                  </div>
                </div>
              </div>

              {/* Mode Cards Skeleton — 3 cards: CAB Mode, Prototype Mode, Build Mode */}
              {[1, 2, 3].map((index) => (
                <div 
                  key={index}
                  className="border border-border-secondary rounded-[12px] p-[21px] flex flex-col gap-[16px] bg-bg-primary"
                >
                  {/* Mode Header Skeleton */}
                  <div className="flex items-start justify-between">
                    <div className="flex gap-[12px] items-center">
                      <div className="skeleton-shimmer rounded-bg-v2-md w-[56px] h-[56px]"></div>
                      <div className="flex flex-col gap-[8px]">
                        <div className="h-5 skeleton-shimmer rounded w-24"></div>
                        <div className="h-4 skeleton-shimmer rounded w-32"></div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-[8px]">
                      <div className="h-5 skeleton-shimmer rounded w-16"></div>
                      <div className="h-4 skeleton-shimmer rounded w-20"></div>
                    </div>
                  </div>

                  {/* AI Runtime Skeleton */}
                  <div className="bg-border-secondary h-px w-full" />
                  <div className="flex items-center justify-between">
                    <div className="h-4 skeleton-shimmer rounded w-20"></div>
                    <div className="h-5 skeleton-shimmer rounded w-16"></div>
                  </div>

                  {/* Token In / Out Skeleton */}
                  <div className="bg-border-secondary h-px w-full" />
                  <div className="flex gap-[24px]">
                    <div className="flex flex-col gap-[4px] flex-1">
                      <div className="h-4 skeleton-shimmer rounded w-14 mx-auto"></div>
                      <div className="h-7 skeleton-shimmer rounded w-16 mx-auto"></div>
                    </div>
                    <div className="flex flex-col gap-[4px] flex-1">
                      <div className="h-4 skeleton-shimmer rounded w-14 mx-auto"></div>
                      <div className="h-7 skeleton-shimmer rounded w-16 mx-auto"></div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {error && (
            <div className="bg-bg-error-primary border border-border-error rounded-bg-v2-lg p-bg-v2-lg">
              <Text variant="text-sm" color="error">
                {error}
              </Text>
            </div>
          )}

          {!loading && !error && tokenStats && (
            <>
              {/* Total Usage Card */}
              <div className="bg-bg-tertiary border border-border-secondary rounded-[12px] p-[21px] flex flex-col gap-[16px]">
                <Text variant="text-md" weight="semibold" color="primary">
                  Total Usage
                </Text>

                {/* Stats Row */}
                <div className="flex gap-[24px] items-start justify-center">
                  {/* Total Cost */}
                  {showCost && (
                    <div className="flex flex-col gap-[4px] items-center flex-1">
                      <Text variant="text-sm" weight="medium" color="quaternary" align="center">
                        Total Cost
                      </Text>
                      <Text variant="display-xs" weight="semibold" color="primary" align="center">
                        {formatCost(tokenStats.total_cost || 0)}
                      </Text>
                    </div>
                  )}

                  {/* Requests */}
                  <div className="flex flex-col gap-[4px] items-center flex-1">
                    <Text variant="text-sm" weight="medium" color="quaternary" align="center">
                      Requests
                    </Text>
                    <Text variant="display-xs" weight="semibold" color="primary" align="center">
                      {tokenStats.total_requests || 0}
                    </Text>
                  </div>
                </div>


                {showDuration && (
                  <div className="border-t border-border-primary pt-[17px]">
                    <div className="flex items-center justify-between">
                      <Text variant="text-sm" weight="medium" color="quaternary">
                        Total AI Runtime
                      </Text>
                      <Text variant="text-xl" weight="semibold" color="primary" align="center">
                        {formatLatency(tokenStats.latency_ms)}
                      </Text>
                    </div>
                  </div>
                )}

                {showTokens && (
                  <div className="border-t border-border-primary pt-[17px] flex gap-[24px]">
                    <div className="flex flex-col gap-[4px] items-center flex-1">
                      <div className="flex items-center gap-[4px]">
                        <img src={ArrowDownRightIcon} alt="" className="w-[14px] h-[14px]" />
                        <Text variant="text-sm" weight="medium" color="quaternary" align="center">
                          Token In
                        </Text>
                      </div>
                      <Text variant="text-xl" weight="semibold" color="primary" align="center">
                        {formatTokenCount(tokenStats.input_tokens || 0)}
                      </Text>
                    </div>
                    <div className="flex flex-col gap-[4px] items-center flex-1">
                      <div className="flex items-center gap-[4px]">
                        <img src={ArrowUpRightIcon} alt="" className="w-[14px] h-[14px]" />
                        <Text variant="text-sm" weight="medium" color="quaternary" align="center">
                          Token Out
                        </Text>
                      </div>
                      <Text variant="text-xl" weight="semibold" color="primary" align="center">
                        {formatTokenCount(tokenStats.output_tokens || 0)}
                      </Text>
                    </div>
                  </div>
                )}
              </div>

              {/* Mode Breakdown Cards — CAB Mode, Prototype Mode, Build Mode */}
              {[
                { mode: 'cab', data: tokenStats },
                { mode: 'ticket_evaluation', data: tokenStats },
                { mode: 'brainstorm', data: tokenStats },
                { mode: 'development', data: tokenStats },
              ].map(({ mode }) => {
                const modeData = mode === 'brainstorm' ? {
                  total_cost: tokenStats.brainstorm_cost,
                  total_requests: tokenStats.brainstorm_requests,
                  total_tokens: tokenStats.brainstorm_tokens,
                  input_tokens: tokenStats.brainstorm_input_tokens,
                  output_tokens: tokenStats.brainstorm_output_tokens,
                  duration: tokenStats.brainstorm_latency_ms,
                } : mode === 'cab' ? {
                  total_cost: tokenStats.cab_cost || 0,
                  total_requests: tokenStats.cab_requests || 0,
                  total_tokens: tokenStats.cab_tokens || 0,
                  input_tokens: tokenStats.cab_input_tokens || 0,
                  output_tokens: tokenStats.cab_output_tokens || 0,
                  duration: tokenStats.cab_latency_ms,
                } : mode === 'ticket_evaluation' ? {
                  total_cost: tokenStats.ticket_evaluation_cost || 0,
                  total_requests: tokenStats.ticket_evaluation_requests || 0,
                  total_tokens: tokenStats.ticket_evaluation_tokens || 0,
                  input_tokens: tokenStats.ticket_evaluation_input_tokens || 0,
                  output_tokens: tokenStats.ticket_evaluation_output_tokens || 0,
                  duration: tokenStats.ticket_evaluation_latency_ms,
                } : {
                  total_cost: tokenStats.development_cost,
                  total_requests: tokenStats.development_requests,
                  total_tokens: tokenStats.development_tokens,
                  input_tokens: tokenStats.development_input_tokens,
                  output_tokens: tokenStats.development_output_tokens,
                  duration: tokenStats.development_latency_ms,
                };

                const displayRequests = modeData.total_requests || 0;

                return (
                  <div 
                    key={mode}
                    className="border border-border-secondary rounded-[12px] p-[21px] flex flex-col gap-[16px]"
                  >
                    {/* Mode Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex gap-[12px] items-center">
                        <div className="bg-bg-tertiary rounded-bg-v2-md w-[56px] h-[56px] flex items-center justify-center">
                          {getModeIcon(mode)}
                        </div>
                        <div className="flex flex-col">
                          <Text variant="text-md" weight="semibold" color="primary">
                            {getModeTitle(mode)}
                          </Text>
                          <Text variant="text-sm" weight="medium" color="quaternary">
                            {getModeDescription(mode)}
                          </Text>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        {showCost && (
                          <Text variant="text-md" weight="semibold" color="primary">
                            {formatCost(modeData.total_cost || 0)}
                          </Text>
                        )}
                        <Text variant="text-sm" weight="medium" color="quaternary" align="right">
                          {displayRequests} requests
                        </Text>
                      </div>
                    </div>

                    {showDuration && (
                      <>
                        <div className="bg-border-secondary h-px w-full" />

                        {/* AI Runtime */}
                        <div className="flex items-center justify-between">
                          <Text variant="text-sm" weight="semibold" color="primary">
                            AI Runtime
                          </Text>
                          <Text variant="text-md" weight="semibold" color="primary">
                            {formatLatency(modeData.duration)}
                          </Text>
                        </div>
                      </>
                    )}

                    {showTokens && (
                      <>
                        <div className="bg-border-secondary h-px w-full" />

                        {/* Token In / Out */}
                        <div className="flex gap-[24px]">
                          <div className="flex flex-col gap-[4px] items-center flex-1">
                            <div className="flex items-center gap-[4px]">
                              <img src={ArrowDownRightIcon} alt="" className="w-[14px] h-[14px]" />
                              <Text variant="text-sm" weight="medium" color="quaternary" align="center">
                                Token In
                              </Text>
                            </div>
                            <Text variant="text-xl" weight="semibold" color="primary" align="center">
                              {formatTokenCount(modeData.input_tokens || 0)}
                            </Text>
                          </div>
                          <div className="flex flex-col gap-[4px] items-center flex-1">
                            <div className="flex items-center gap-[4px]">
                              <img src={ArrowUpRightIcon} alt="" className="w-[14px] h-[14px]" />
                              <Text variant="text-sm" weight="medium" color="quaternary" align="center">
                                Token Out
                              </Text>
                            </div>
                            <Text variant="text-xl" weight="semibold" color="primary" align="center">
                              {formatTokenCount(modeData.output_tokens || 0)}
                            </Text>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectStatistics;

